import {Button} from '@dropbox/dig-components/buttons';
import {Toggle} from '@dropbox/dig-components/controls';
import {FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {Modal} from '@dropbox/dig-components/modal';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getTableNodeFromLexicalNodeOrThrow} from '@lexical/table';
import {CancelButton} from 'components/DSYS/CancelButton';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {t} from 'i18next';
import {useCallback, useEffect, useState} from 'react';

import {TableCellSettings} from './EnhancedTableCellNode';
import {$isEnhancedTableNode} from './EnhancedTableNode';
import {getTableCellNodeById} from './TableCellNodeUtils';

interface TableCellSettingsModalProps {
  cellId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const TableCellSettingsModal = ({cellId, isOpen, onClose}: TableCellSettingsModalProps) => {
  const [editor] = useLexicalComposerContext();
  const [settings, setSettings] = useState<TableCellSettings>({});
  const [initialGuidance, setInitialGuidance] = useState('');
  const [updatedGuidance, setUpdatedGuidance] = useState('');
  const [addRowsDisabled, setAddRowsDisabled] = useState(false);
  const [addColumnsDisabled, setAddColumnsDisabled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load current settings when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoaded(false);
      editor.getEditorState().read(() => {
        const cellNode = getTableCellNodeById(cellId);
        if (cellNode) {
          const currentSettings = cellNode.getSettings();
          setSettings(currentSettings);
          const guidance = currentSettings.guidance || '';
          setInitialGuidance(guidance);
          setUpdatedGuidance(guidance);

          // Get addRowsDisabled and addColumnsDisabled from the table node, not the cell
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
          if ($isEnhancedTableNode(tableNode)) {
            setAddRowsDisabled(tableNode.getAddRowsDisabled());
            setAddColumnsDisabled(tableNode.getAddColumnsDisabled());
          } else {
            setAddRowsDisabled(false);
            setAddColumnsDisabled(false);
          }

          setIsLoaded(true);
        } else {
          // If no cell node found, still set loaded to true so UI shows
          setIsLoaded(true);
        }
      });
    } else {
      // Reset state when modal closes
      setIsLoaded(false);
      setInitialGuidance('');
      setUpdatedGuidance('');
      setSettings({});
    }
  }, [isOpen, cellId, editor]);

  const handleSave = useCallback(() => {
    editor.update(() => {
      const cellNode = getTableCellNodeById(cellId);
      if (cellNode) {
        // Save cell-specific settings
        const updatedSettings: TableCellSettings = {
          ...settings,
          guidance: updatedGuidance.trim() || undefined,
        };
        cellNode.setSettings(updatedSettings);

        // Save addRowsDisabled and addColumnsDisabled to the table node
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
        if ($isEnhancedTableNode(tableNode)) {
          tableNode.setAddRowsDisabled(addRowsDisabled);
          tableNode.setAddColumnsDisabled(addColumnsDisabled);
        }
      }
    });
    onClose();
  }, [editor, cellId, settings, updatedGuidance, addRowsDisabled, addColumnsDisabled, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={isOpen}
      isCentered
      withCloseButton="Close"
      aria-label="Table cell settings"
      onRequestClose={onClose}
    >
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title>Table Cell Settings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormRow>
          <FormLabel htmlFor="guidance">Guidance Text</FormLabel>
          {isLoaded ? (
            <RichTextArea
              value={initialGuidance}
              onChange={(state) => setUpdatedGuidance(JSON.stringify(state))}
              editable
              placeholder={t('start_writing_placeholder')}
              source="table-cell-guidance"
              hideToolbar
            />
          ) : null}
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="add-rows-disabled">Disable Adding Rows</FormLabel>
          <Toggle
            isToggled={addRowsDisabled}
            onClick={() => setAddRowsDisabled(!addRowsDisabled)}
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="add-columns-disabled">Disable Adding Columns</FormLabel>
          <Toggle
            isToggled={addColumnsDisabled}
            onClick={() => setAddColumnsDisabled(!addColumnsDisabled)}
          />
        </FormRow>
        {/* AI Rules section can be added here later */}
      </Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
